import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import PageHeader from "../components/PageHeader";
import MiniDashboard from "../components/MiniDashboard";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Plus, Trash2, Users, ChevronRight, Award, TrendingUp, UserCog, Package } from "lucide-react";

export default function Parties() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", address: "", employee_id: "", notes: "" });
  const [filterEmployee, setFilterEmployee] = useState("");

  const load = async () => {
    setLoading(true);
    const [p, e, o] = await Promise.all([
      api.get("/parties"), api.get("/employees"), api.get("/orders"),
    ]);
    setItems(p.data); setEmployees(e.data); setOrders(o.data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/parties", {
        name: form.name, phone: form.phone, address: form.address,
        employee_id: form.employee_id || null, notes: form.notes,
      });
      toast.success("Buyer added");
      setOpen(false);
      setForm({ name: "", phone: "", address: "", employee_id: "", notes: "" });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const del = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Move this buyer to the Recycle Bin? You can restore within 45 days.")) return;
    await api.delete(`/parties/${id}`);
    toast.success("Moved to Recycle Bin");
    load();
  };

  const stats = useMemo(() => {
    let units = 0;
    const now = new Date();
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
    let thisMonth = 0;
    const map = {};
    for (const o of orders) {
      const qty = (o.items || []).reduce((n, i) => n + (i.quantity || 0), 0);
      units += qty;
      map[o.party_name] = (map[o.party_name] || 0) + 1;
      if (new Date(o.received_at) >= monthStart) thisMonth += 1;
    }
    const ranked = Object.entries(map).sort((a, b) => b[1] - a[1]);
    return { units, thisMonth, top: ranked[0]?.[0] || "—" };
  }, [orders]);

  const filtered = filterEmployee
    ? items.filter((it) => it.employee_id === filterEmployee)
    : items;

  return (
    <div data-testid="party-page">
      <PageHeader
        title="Buyers"
        subtitle="Each buyer is linked to an employee — click a row to see full history"
        action={
          <button
            onClick={() => setOpen(true)}
            data-testid="add-party-btn"
            className="btn-primary flex items-center gap-2"
          >
            <Plus className="h-5 w-5" /> Add Buyer
          </button>
        }
      />

      <MiniDashboard
        testid="party-mini-dashboard"
        stats={[
          { icon: Users, label: "Total Buyers", value: items.length, tint: "forest" },
          { icon: Package, label: "Units Ordered", value: stats.units.toLocaleString("en-IN"), tint: "info" },
          { icon: TrendingUp, label: "Orders this month", value: stats.thisMonth, tint: "accent" },
          { icon: Award, label: "Top Buyer", value: stats.top, tint: "forest" },
        ]}
      />

      {/* Employee filter */}
      <div className="card-surface p-4 mb-6 flex flex-wrap items-center gap-3">
        <label className="text-sm font-figtree font-medium text-ink flex items-center gap-2">
          <UserCog className="h-4 w-4 text-ink-soft" /> Filter by employee:
        </label>
        <select
          data-testid="party-employee-filter"
          value={filterEmployee}
          onChange={(e) => setFilterEmployee(e.target.value)}
          className="h-10 px-3 rounded-lg border border-line bg-white outline-none focus:ring-2 focus:ring-forest text-sm"
        >
          <option value="">All buyers ({items.length})</option>
          {employees.map((e) => {
            const c = items.filter((it) => it.employee_id === e.id).length;
            return <option key={e.id} value={e.id}>{e.name} ({c})</option>;
          })}
        </select>
        {filterEmployee && (
          <button
            onClick={() => setFilterEmployee("")}
            className="text-sm text-ink-soft hover:text-forest font-figtree"
          >
            Clear
          </button>
        )}
      </div>

      <div className="card-surface overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-ink-soft">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center">
            <div className="mx-auto h-12 w-12 rounded-xl bg-forest-soft text-forest grid place-items-center mb-3">
              <Users className="h-6 w-6" />
            </div>
            <div className="font-outfit font-semibold text-ink">No buyers to show</div>
            <div className="text-ink-soft text-sm font-figtree">{filterEmployee ? "This employee has no assigned buyers." : "Click 'Add Buyer' to add your first one."}</div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-sand/60">
                <tr>
                  <th className="text-left font-figtree font-semibold text-sm text-ink-soft px-6 py-4">Name</th>
                  <th className="text-left font-figtree font-semibold text-sm text-ink-soft px-6 py-4">Assigned Employee</th>
                  <th className="text-left font-figtree font-semibold text-sm text-ink-soft px-6 py-4">Phone</th>
                  <th className="text-left font-figtree font-semibold text-sm text-ink-soft px-6 py-4">Address</th>
                  <th className="text-right font-figtree font-semibold text-sm text-ink-soft px-6 py-4"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((it) => (
                  <tr
                    key={it.id}
                    className="border-t border-line hover:bg-bone/60 cursor-pointer"
                    onClick={() => nav(`/parties/${it.id}`)}
                    data-testid={`party-row-${it.id}`}
                  >
                    <td className="px-6 py-4 font-figtree font-medium text-ink">
                      <div className="flex items-center gap-2">
                        <span>{it.name}</span>
                        <ChevronRight className="h-4 w-4 text-ink-mute" />
                      </div>
                    </td>
                    <td className="px-6 py-4 font-figtree text-ink-soft text-sm">
                      {it.employee_name ? (
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-forest-soft text-forest text-xs font-semibold">
                          {it.employee_name}
                        </span>
                      ) : "—"}
                    </td>
                    <td className="px-6 py-4 font-figtree text-ink-soft">{it.phone || "—"}</td>
                    <td className="px-6 py-4 font-figtree text-ink-soft text-sm">{it.address || "—"}</td>
                    <td className="px-6 py-4 text-right">
                      <button
                        onClick={(e) => del(it.id, e)}
                        className="p-2 rounded-lg text-ink-soft hover:bg-red-50 hover:text-red-700 transition-colors duration-200"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-outfit">Add Buyer</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-4 mt-2">
            <div>
              <label className="text-sm font-figtree font-medium text-ink block mb-2">Name</label>
              <input
                data-testid="party-name-input"
                required value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full h-12 px-4 rounded-lg border border-line outline-none focus:ring-2 focus:ring-forest"
              />
            </div>
            <div>
              <label className="text-sm font-figtree font-medium text-ink block mb-2">Assigned Employee</label>
              <select
                data-testid="party-employee-select"
                value={form.employee_id}
                onChange={(e) => setForm({ ...form, employee_id: e.target.value })}
                className="w-full h-12 px-4 rounded-lg border border-line bg-white outline-none focus:ring-2 focus:ring-forest"
              >
                <option value="">None</option>
                {employees.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-sm font-figtree font-medium text-ink block mb-2">Phone</label>
              <input
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="w-full h-12 px-4 rounded-lg border border-line outline-none focus:ring-2 focus:ring-forest"
              />
            </div>
            <div>
              <label className="text-sm font-figtree font-medium text-ink block mb-2">Address</label>
              <textarea
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                rows={2}
                className="w-full px-4 py-3 rounded-lg border border-line outline-none focus:ring-2 focus:ring-forest"
              />
            </div>
            <div>
              <label className="text-sm font-figtree font-medium text-ink block mb-2">Notes</label>
              <input
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="w-full h-12 px-4 rounded-lg border border-line outline-none focus:ring-2 focus:ring-forest"
              />
            </div>
            <DialogFooter>
              <button type="button" onClick={() => setOpen(false)} className="px-4 py-2 rounded-lg border border-line font-figtree">Cancel</button>
              <button data-testid="party-submit-btn" type="submit" className="btn-primary">Save</button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
